/**
 * Envolvente de un WAV: cuanta fuerza tiene la voz en cada instante.
 *
 * La usa la onda de sonido del kiosco para moverse al ritmo de lo que dice
 * Androcles. Es puro calculo sobre los bytes del WAV que ya viaja por el
 * socket, asi que no hace falta ni WebAudio ni GPU: por eso la onda funciona
 * donde el lip-sync no llegaba (MuseTalk iba a 2,9x tiempo real en la 3060).
 *
 * Se admite PCM 16 bits, que es lo que generan Piper (22 kHz) y Kokoro
 * (24 kHz). De un formato que no se entienda se devuelve una envolvente vacia
 * y la onda late sola: mejor eso que una animacion a tirones.
 */

/** Numero de tramos por segundo de la envolvente (~25 = un tramo por frame). */
const TRAMOS_POR_SEGUNDO = 25;

/** Resultado del analisis de un WAV. */
export interface Envolvente {
  /** Valores 0..1, uno por tramo, en orden. */
  valores: number[];
  /** Duracion total del audio en segundos (0 si no se pudo leer). */
  duracion: number;
}

/** Lee una cabecera WAV y devuelve donde empiezan los datos y su formato. */
function _cabecera(
  vista: DataView,
): { inicio: number; bytes: number; canales: number; frecuencia: number; bits: number } | null {
  if (vista.byteLength < 44) return null;
  // "RIFF" .... "WAVE"
  if (vista.getUint32(0, false) !== 0x52494646) return null;
  if (vista.getUint32(8, false) !== 0x57415645) return null;

  let posicion = 12;
  let canales = 1;
  let frecuencia = 22_050;
  let bits = 16;

  while (posicion + 8 <= vista.byteLength) {
    const id = vista.getUint32(posicion, false);
    const tam = vista.getUint32(posicion + 4, true);
    const cuerpo = posicion + 8;

    if (id === 0x666d7420 /* "fmt " */ && cuerpo + 16 <= vista.byteLength) {
      canales = vista.getUint16(cuerpo + 2, true) || 1;
      frecuencia = vista.getUint32(cuerpo + 4, true) || 22_050;
      bits = vista.getUint16(cuerpo + 14, true) || 16;
    } else if (id === 0x64617461 /* "data" */) {
      const bytes = Math.min(tam, vista.byteLength - cuerpo);
      return { inicio: cuerpo, bytes, canales, frecuencia, bits };
    }
    // Los trozos van alineados a 2 bytes.
    posicion = cuerpo + tam + (tam % 2);
  }
  return null;
}

/**
 * Calcula la envolvente de un WAV PCM 16 bits.
 *
 * @param wav - bytes del WAV tal como llegan por el socket.
 * @returns Valores 0..1 por tramo y la duracion en segundos.
 */
export function envolventeDeWav(wav: ArrayBuffer): Envolvente {
  const vacia: Envolvente = { valores: [], duracion: 0 };
  if (!wav || wav.byteLength < 44) return vacia;

  const vista = new DataView(wav);
  const cabecera = _cabecera(vista);
  if (!cabecera || cabecera.bits !== 16) return vacia;

  const { inicio, bytes, canales, frecuencia } = cabecera;
  const muestras = Math.floor(bytes / 2);
  if (muestras <= 0) return vacia;

  const porTramo = Math.max(1, Math.floor(frecuencia / TRAMOS_POR_SEGUNDO) * canales);
  const valores: number[] = [];

  let suma = 0;
  let cuenta = 0;
  let pico = 0;

  for (let i = 0; i < muestras; i += 1) {
    const valor = vista.getInt16(inicio + i * 2, true) / 32768;
    suma += valor * valor;
    cuenta += 1;
    if (cuenta >= porTramo) {
      const rms = Math.sqrt(suma / cuenta);
      valores.push(rms);
      pico = Math.max(pico, rms);
      suma = 0;
      cuenta = 0;
    }
  }
  if (cuenta > 0) {
    const rms = Math.sqrt(suma / cuenta);
    valores.push(rms);
    pico = Math.max(pico, rms);
  }

  // Normalizar al pico: lo que importa es el RITMO de la frase, no su volumen
  // absoluto. Sin esto, una voz baja apenas movia la onda.
  if (pico > 0) {
    for (let i = 0; i < valores.length; i += 1) {
      valores[i] = Math.min(1, valores[i]! / pico);
    }
  }

  return {
    valores,
    duracion: muestras / canales / frecuencia,
  };
}
